import { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import {
  CrewAllotmentRecord,
  CrewClaim,
  CrewDeduction,
  SignOnOffRecord,
  getCrewAllotments,
  deleteCrewSignature,
  getCrewList,
  getCrewClaims,
  getCrewDeductions,
  getCrewLatestBankingDetails,
  getCrewSignature,
  getRanks,
  saveCrewSignature,
  getSignOnOffRecords,
} from '../core/_requests'
import { useAuth } from '../../auth'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export type SalarySlipLineItem = {
  label: string
  amount: number
  pbAmount: number
}

export type SalarySlipCrewOption = {
  crewId: number
  crewName: string
  rankName?: string
  vesselName?: string
}

type SalarySlipModalProps = {
  isOpen: boolean
  onClose: () => void
  selectedPeriod?: string
  crewOptions: SalarySlipCrewOption[]
  defaultCrewId?: number
}

const MILLIS_IN_DAY = 24 * 60 * 60 * 1000

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const round2 = (value: number): number => Number(value.toFixed(2))

const currentMonthPeriod = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

const daysBetweenDates = (from?: string | null, to?: string | null): number => {
  const fromDate = parseIsoDateOnly(from)
  const toDate = parseIsoDateOnly(to)
  if (!fromDate || !toDate) return 0
  const diff = Math.floor((toDate.getTime() - fromDate.getTime()) / MILLIS_IN_DAY)
  return Math.max(0, diff)
}

const monthToClaimDate = (period?: string) => {
  if (!period) return ''
  const [yearText, monthText] = period.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return ''
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`
}

const monthKey = (value?: string | null): string => (value || '').toString().slice(0, 7)

const parseIsoDateOnly = (value?: string | null): Date | null => {
  if (!value) return null
  const datePart = value.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(Date.UTC(year, month - 1, day))
}

const buildMonthRange = (claimMonth?: string | null) => {
  const monthDate = parseIsoDateOnly(claimMonth)
  if (!monthDate) return null
  const year = monthDate.getUTCFullYear()
  const month = monthDate.getUTCMonth()
  const start = new Date(Date.UTC(year, month, 1))
  const end = new Date(Date.UTC(year, month + 1, 0))
  return { start, end, daysInMonth: end.getUTCDate() }
}

const overlapsMonth = (record: SignOnOffRecord, claimMonth?: string | null) => {
  const monthRange = buildMonthRange(claimMonth)
  if (!monthRange) return false

  const signOn = parseIsoDateOnly(record.signOnDate)
  if (!signOn) return false
  const signOff = parseIsoDateOnly(record.signOffDate) || monthRange.end

  const overlapStart = signOn > monthRange.start ? signOn : monthRange.start
  const overlapEnd = signOff < monthRange.end ? signOff : monthRange.end
  return overlapEnd >= overlapStart
}

const daysWorkedInMonth = (
  records: SignOnOffRecord[],
  crewId: number,
  claimMonth?: string | null
): number => {
  const monthRange = buildMonthRange(claimMonth)
  if (!monthRange || !crewId) return 0

  let totalDays = 0

  records.forEach((record) => {
    if (Number(record.crewId) !== Number(crewId)) return

    const signOn = parseIsoDateOnly(record.signOnDate)
    if (!signOn) return

    const signOff = parseIsoDateOnly(record.signOffDate) || monthRange.end
    const overlapStart = signOn > monthRange.start ? signOn : monthRange.start
    const overlapEnd = signOff < monthRange.end ? signOff : monthRange.end

    if (overlapEnd < overlapStart) return

    const days = Math.floor((overlapEnd.getTime() - overlapStart.getTime()) / MILLIS_IN_DAY) + 1
    totalDays += days
  })

  return Math.max(0, Math.min(monthRange.daysInMonth, totalDays))
}

const pbDaysFromSignOn = (record: SignOnOffRecord | undefined, claimMonth?: string | null): number => {
  if (!record) return 0
  const signOn = parseIsoDateOnly(record.signOnDate)
  if (!signOn) return 0

  const monthRange = buildMonthRange(claimMonth)
  const monthEnd = monthRange?.end
  const signOff = parseIsoDateOnly(record.signOffDate)

  let effectiveEnd = signOff || monthEnd
  if (!effectiveEnd) return 0
  if (monthEnd && effectiveEnd > monthEnd) {
    effectiveEnd = monthEnd
  }

  const diff = Math.floor((effectiveEnd.getTime() - signOn.getTime()) / MILLIS_IN_DAY) + 1
  return Math.max(0, diff)
}

const isAllocationSummaryRow = (row: CrewAllotmentRecord): boolean => {
  const remarks = (row.remarks || '').toString().trim().toLowerCase()
  return remarks.startsWith('allocations -')
}

const formatMonthTitle = (period?: string) => {
  if (!period) return '-'
  const [yearText, monthText] = period.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return period
  return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

const formatDate = (value?: string | null) => {
  const date = parseIsoDateOnly(value)
  if (!date) return '--'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const firstNumber = (source: any, keys: string[]): number => {
  for (const key of keys) {
    if (source?.[key] != null) {
      const parsed = Number(source[key])
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return 0
}

const sumKeys = (source: any, keys: string[]): number => {
  return keys.reduce((sum, key) => sum + toNumber(source?.[key]), 0)
}

const resolveCurrencyCode = (accountCurrency?: string | null, otherCurrency?: string | null) => {
  const normalizedAccountCurrency = (accountCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (otherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || 'OTHER'
  return normalizedAccountCurrency || 'USD'
}

const isMasterEquivalentRank = (rankId?: number | null, rankName?: string | null): boolean => {
  const normalizedName = (rankName || '').toLowerCase()
  return Number(rankId) === 1 || normalizedName.includes('master') || normalizedName.includes('captain')
}

const toMoney = (value: number) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

type SlipData = {
  crewName: string
  rankName: string
  vesselName: string
  currencyCode: string
  dateOfJoining: string
  signOnDate: string
  signOnPort: string
  signOffDate: string
  signOffPort: string
  signOffReason: string
  arrivalHomeport: string
  accountNumber: string
  beneficiary: string
  paidDate: string
  transactionRef: string
  earnings: SalarySlipLineItem[]
  allotmentAmount: number
  totalDeductions: number
  earningsTotal: number
  deductionsGrandTotal: number
  balanceCarriedForward: number
}

const SalarySlipModal: FC<SalarySlipModalProps> = ({
  isOpen,
  onClose,
  selectedPeriod,
  crewOptions,
  defaultCrewId,
}) => {
  const { currentUser } = useAuth()
  const [selectedCrewId, setSelectedCrewId] = useState<number>(0)
  const [selectedMonthPeriod, setSelectedMonthPeriod] = useState<string>(
    (selectedPeriod || '').slice(0, 7) || currentMonthPeriod()
  )
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [slipData, setSlipData] = useState<SlipData | null>(null)
  const [isPdfGenerating, setIsPdfGenerating] = useState(false)
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>('')
  const [pendingSignatureDataUrl, setPendingSignatureDataUrl] = useState<string>('')
  const [signatureFileName, setSignatureFileName] = useState<string>('')
  const [signatureMessage, setSignatureMessage] = useState<string>('')
  const [isSignatureLoading, setIsSignatureLoading] = useState(false)
  const [isSignatureSaving, setIsSignatureSaving] = useState(false)

  const { isCrew } = useCrewingRoleAccess()

  const canManageSignature = useMemo(() => {
    const roleId = Number(currentUser?.role?.id ?? 0)
    const rankId = Number((currentUser as any)?.rank?.id ?? 0)
    const rankName = String((currentUser as any)?.rank?.rank ?? '')
    return (roleId === 4 || isCrew) && isMasterEquivalentRank(rankId, rankName)
  }, [currentUser, isCrew])

  const claimMonth = useMemo(() => monthToClaimDate(selectedMonthPeriod), [selectedMonthPeriod])

  const safeFilePart = (value: string) => value.replace(/[^a-z0-9_-]+/gi, '_')

  const handleDownloadPdf = async () => {
    if (!slipData || isPdfGenerating) return

    setIsPdfGenerating(true)
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const marginX = 40

      doc.setFontSize(18)
      doc.text('Salary Slip', marginX, 42)
      doc.setFontSize(10)
      doc.setTextColor(90, 90, 90)
      doc.text(`Vessel: ${slipData.vesselName}`, marginX, 60)
      doc.text(`Period: ${formatMonthTitle(selectedMonthPeriod)}`, marginX, 74)
      doc.text(`Currency: ${slipData.currencyCode}`, marginX, 88)

      doc.setTextColor(0, 0, 0)
      doc.setFontSize(10)
      doc.text(`Crew: ${slipData.crewName}`, pageWidth - marginX - 180, 60)
      doc.text(`Rank: ${slipData.rankName}`, pageWidth - marginX - 180, 74)
      doc.text(`Sign-On: ${slipData.signOnDate} (${slipData.signOnPort})`, pageWidth - marginX - 180, 88)

        ; (doc as any).autoTable({
          startY: 108,
          head: [['Earnings', 'Amount', 'Cont-to-PB Date']],
          body: [
            ...slipData.earnings.map((item) => [item.label, toMoney(item.amount), toMoney(item.pbAmount)]),
            ['Total', toMoney(slipData.earningsTotal), toMoney(slipData.earningsTotal)],
          ],
          styles: { fontSize: 9, cellPadding: 4 },
          headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [30, 30, 30] },
          margin: { left: marginX, right: marginX },
          theme: 'grid',
        })

      const afterEarningsY = (doc as any).lastAutoTable?.finalY || 290

        ; (doc as any).autoTable({
          startY: afterEarningsY + 14,
          head: [['Deductions', 'Amount']],
          body: [
            ['Allotment', toMoney(slipData.allotmentAmount)],
            ['Total Deductions', toMoney(slipData.totalDeductions)],
            ['Grand Total', toMoney(slipData.deductionsGrandTotal)],
          ],
          styles: { fontSize: 9, cellPadding: 4 },
          headStyles: { fillColor: [204, 83, 44], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [30, 30, 30] },
          margin: { left: marginX, right: marginX },
          theme: 'grid',
        })

      const afterDeductionsY = (doc as any).lastAutoTable?.finalY || afterEarningsY + 130

      doc.setFontSize(10)
      doc.setTextColor(20, 20, 20)
      doc.text(
        `Balance carried forward to next month: ${toMoney(slipData.balanceCarriedForward)}`,
        marginX,
        afterDeductionsY + 20
      )
      const signatureBaseY = Math.min(afterDeductionsY + 90, doc.internal.pageSize.getHeight() - 55)
      const signatureLineStartX = pageWidth - marginX - 190
      const signatureLineEndX = pageWidth - marginX - 20

      if (signatureDataUrl) {
        try {
          doc.addImage(signatureDataUrl, 'PNG', pageWidth - marginX - 180, signatureBaseY - 62, 160, 48)
        } catch (imageError) {
          console.warn('Unable to render signature image in PDF:', imageError)
        }
      }

      doc.setDrawColor(80, 80, 80)
      doc.line(signatureLineStartX, signatureBaseY, signatureLineEndX, signatureBaseY)
      doc.setFontSize(10)
      doc.text('Master Signature', signatureLineStartX + 5, signatureBaseY + 14)

      const fileName = `${safeFilePart(slipData.crewName)}_salary_slip_${safeFilePart(selectedMonthPeriod || 'period')}.pdf`
      doc.save(fileName)
      toast.success('Salary slip PDF downloaded successfully')
    } catch (error) {
      console.error('Failed to generate salary slip PDF:', error)
      showErrorToast(error, 'Failed to generate salary slip PDF', 'load')
    } finally {
      setIsPdfGenerating(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const fallbackCrewId = defaultCrewId || crewOptions[0]?.crewId || 0
    setSelectedCrewId(fallbackCrewId)
  }, [isOpen, defaultCrewId, crewOptions])

  useEffect(() => {
    if (!isOpen) return
    setSelectedMonthPeriod((selectedPeriod || '').slice(0, 7) || currentMonthPeriod())
  }, [isOpen, selectedPeriod])

  useEffect(() => {
    if (!isOpen || !selectedCrewId) return

    let isMounted = true
    const loadSignature = async () => {
      setIsSignatureLoading(true)
      try {
        const response = await getCrewSignature(selectedCrewId)
        if (!isMounted) return
        const dataUrl = response?.dataUrl || ''
        setSignatureDataUrl(dataUrl)
        setPendingSignatureDataUrl(dataUrl)
        setSignatureFileName('')
        setSignatureMessage('')
      } catch (error) {
        if (!isMounted) return
        console.error('Error loading signature:', error)
        setSignatureDataUrl('')
        setPendingSignatureDataUrl('')
        setSignatureFileName('')
        setSignatureMessage('')
      } finally {
        if (isMounted) setIsSignatureLoading(false)
      }
    }

    loadSignature()
    return () => {
      isMounted = false
    }
  }, [isOpen, selectedCrewId])

  const handleSignatureFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setSignatureMessage('Please upload a valid image file.')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : ''
      if (!dataUrl) {
        setSignatureMessage('Unable to read selected file.')
        return
      }
      setPendingSignatureDataUrl(dataUrl)
      setSignatureFileName(file.name)
      setSignatureMessage('Preview updated. Click Save to apply this signature.')
    }
    reader.onerror = () => {
      setSignatureMessage('Unable to read selected file.')
    }
    reader.readAsDataURL(file)
  }

  const handleSaveSignature = async () => {
    if (!canManageSignature) {
      setSignatureMessage('Only Master can upload signature.')
      return
    }
    if (!selectedCrewId) return
    if (!pendingSignatureDataUrl) {
      setSignatureMessage('Please choose a signature image first.')
      return
    }

    setIsSignatureSaving(true)
    try {
      const response = await saveCrewSignature(selectedCrewId, pendingSignatureDataUrl)
      const savedDataUrl = response?.dataUrl || pendingSignatureDataUrl
      setSignatureDataUrl(savedDataUrl)
      setPendingSignatureDataUrl(savedDataUrl)
      setSignatureMessage('Signature saved successfully.')
      toast.success('Signature saved successfully')
    } catch (error) {
      console.error('Error saving signature:', error)
      setSignatureMessage('Unable to save signature right now.')
      showErrorToast(error, 'Unable to save signature right now.', 'save')
    } finally {
      setIsSignatureSaving(false)
    }
  }

  const handleRemoveSignature = async () => {
    if (!canManageSignature) {
      setSignatureMessage('Only Master can remove signature.')
      return
    }
    if (!selectedCrewId) return

    setIsSignatureSaving(true)
    try {
      await deleteCrewSignature(selectedCrewId)
      setSignatureDataUrl('')
      setPendingSignatureDataUrl('')
      setSignatureFileName('')
      setSignatureMessage('Signature removed.')
      toast.success('Signature removed successfully')
    } catch (error) {
      console.error('Error removing signature:', error)
      setSignatureMessage('Unable to remove signature right now.')
      showErrorToast(error, 'Unable to remove signature right now.', 'delete')
    } finally {
      setIsSignatureSaving(false)
    }
  }

  useEffect(() => {
    if (!isOpen || !selectedCrewId || !claimMonth) return

    let isMounted = true
    const loadSalarySlip = async () => {
      setIsLoading(true)
      setErrorMessage('')

      try {
        const [banking, claims, deductions, allotments, signRecords, crewList, rankList] = await Promise.all([
          getCrewLatestBankingDetails(selectedCrewId),
          getCrewClaims().catch(() => [] as CrewClaim[]),
          getCrewDeductions().catch(() => [] as CrewDeduction[]),
          getCrewAllotments(selectedCrewId, selectedMonthPeriod || undefined).catch(
            async () => (await getCrewAllotments(selectedCrewId).catch(() => [] as CrewAllotmentRecord[]))
          ),
          getSignOnOffRecords().catch(() => [] as SignOnOffRecord[]),
          getCrewList().catch(() => [] as any[]),
          getRanks().catch(() => [] as any[]),
        ])

        if (!isMounted) return

        const selectedCrew = crewOptions.find((crew) => crew.crewId === selectedCrewId)
        const selectedCrewLive = (crewList || []).find((crew: any) => Number(crew.id) === selectedCrewId)
        const rankNameLive =
          (rankList || []).find((rank: any) => Number(rank.id) === Number(selectedCrewLive?.rankId))?.rank ||
          selectedCrew?.rankName ||
          '-'
        const vesselNameLive =
          selectedCrewLive?.vessel?.fleet_name || selectedCrewLive?.vesselName || selectedCrew?.vesselName || '-'
        const crewNameLive = selectedCrewLive?.name || selectedCrew?.crewName || `Crew ${selectedCrewId}`
        const bankingAny = banking as any
        const currencyCode = resolveCurrencyCode(bankingAny?.accountCurrency, bankingAny?.otherCurrency)

        const basicWages = firstNumber(bankingAny, ['totalBasic', 'basicWages', 'basic'])
        const overtimeWages = firstNumber(bankingAny, [
          'overtimePay',
          'fixedOvertime109Hrs',
          'overtimeWages',
        ])
        const leaveWages = firstNumber(bankingAny, ['leavePay', 'leaveWages9DaysPerMonth', 'leaveWages'])

        const claimsForMonth = claims.filter(
          (claim) => Number(claim.crewId) === selectedCrewId && monthKey(claim.claimMonth) === monthKey(claimMonth)
        )
        const deductionsForMonth = deductions.filter(
          (row) => Number(row.crewId) === selectedCrewId && monthKey(row.claimMonth) === monthKey(claimMonth)
        )

        const explicitOther = sumKeys(bankingAny, [
          'incentive',
          'subSmb',
          'subSMB',
          'tradeAllowance',
          'otherAllowance',
          'ownersLoyaltyBonus',
          'rejoiningBonus',
          'gratuity',
          'specialAllowance',
          'otherSalaryComponents',
        ])
        const salaryFallback = firstNumber(bankingAny, ['salary'])
        const derivedOther = Math.max(0, salaryFallback - basicWages - overtimeWages - leaveWages)
        const claimOtAmount = claimsForMonth.reduce((sum, claim) => sum + toNumber(claim.overtimeAmount), 0)
        const otherWagesBase = explicitOther > 0 ? explicitOther : derivedOther
        const otherWages = round2(otherWagesBase + claimOtAmount)

        const hra = claimsForMonth.reduce((sum, claim) => sum + toNumber(claim.hraAllowance), 0)
        const reimbursements = claimsForMonth.reduce(
          (sum, claim) =>
            sum +
            toNumber(claim.medical) +
            toNumber(claim.safetyGear) +
            toNumber(claim.travel) +
            toNumber(claim.boardingLodging) +
            toNumber(claim.bonus) +
            toNumber(claim.miscReimbursements),
          0
        )

        const totalDeductions = deductionsForMonth.reduce((sum, row) => {
          const byFields =
            toNumber(row.deductionCashAdvanceFromOffice) +
            toNumber(row.deductionBond) +
            toNumber(row.deductionOtherDeductions) +
            toNumber(row.deductionInternetCard) +
            toNumber(row.firstTimeDeduction) +
            toNumber(row.unionSocialDues)
          const effective = byFields > 0 ? byFields : toNumber(row.deductionsTotal)
          return sum + effective
        }, 0)

        const summaryAllotmentAmount = allotments
          .filter((row) => isAllocationSummaryRow(row))
          .reduce((sum, row) => sum + toNumber(row.allotmentAmount ?? row.amount), 0)

        const detailedAllotmentAmount = allotments
          .filter((row) => !isAllocationSummaryRow(row))
          .reduce((sum, row) => sum + toNumber(row.allotmentAmount ?? row.amount), 0)

        // Prefer the Crew Allotment page total (summary row) when present.
        const allotmentAmount = summaryAllotmentAmount > 0 ? summaryAllotmentAmount : detailedAllotmentAmount

        const crewRecords = signRecords.filter((record) => Number(record.crewId) === selectedCrewId)
        const monthRecords = crewRecords.filter((record) => overlapsMonth(record, claimMonth))
        const signOnRecord = [...monthRecords].sort((a, b) => (a.signOnDate || '').localeCompare(b.signOnDate || ''))[0]
        const signOffRecord = [...monthRecords]
          .filter((record) => Boolean(record.signOffDate))
          .sort((a, b) => (b.signOffDate || '').localeCompare(a.signOffDate || ''))[0]
        const firstEverSignOn = [...crewRecords].sort((a, b) => (a.signOnDate || '').localeCompare(b.signOnDate || ''))[0]
        const dateOfJoiningRaw =
          signOnRecord?.dateOfAgreement ||
          firstEverSignOn?.dateOfAgreement ||
          selectedCrewLive?.dateOfJoining ||
          selectedCrewLive?.date_of_joining ||
          bankingAny?.dateOfJoining ||
          bankingAny?.date_of_joining ||
          firstEverSignOn?.signOnDate ||
          null

        const monthRange = buildMonthRange(claimMonth)
        const workedDays = pbDaysFromSignOn(signOnRecord, claimMonth)
        const pbDaysInMonth = monthRange
          ? monthRange.start.getUTCMonth() === 1
            ? monthRange.daysInMonth
            : 30
          : 30
        const pbFactor = pbDaysInMonth ? workedDays / pbDaysInMonth : 1
        const monthIdentifier = monthKey(claimMonth)
        const isJoiningInSelectedMonth = monthKey(signOnRecord?.signOnDate) === monthIdentifier
        const isSignOffInSelectedMonth = monthKey(signOffRecord?.signOffDate) === monthIdentifier
        const joiningAllowanceDivisor = 30
        const joiningDaysBeforeSignOn = isJoiningInSelectedMonth
          ? daysBetweenDates(dateOfJoiningRaw, signOnRecord?.signOnDate)
          : 0
        const joiningAllowance =
          joiningDaysBeforeSignOn > 0
            ? round2(joiningDaysBeforeSignOn * (basicWages / joiningAllowanceDivisor))
            : 0

        // Sign-off allowance is paid as 2 extra days at monthly daily wage.
        const signOffAllowance = isSignOffInSelectedMonth
          ? round2((basicWages / joiningAllowanceDivisor) * 2)
          : 0

        const allowanceRows: SalarySlipLineItem[] = []
        if (joiningAllowance > 0) {
          allowanceRows.push({
            label: 'Joining Allowance',
            amount: joiningAllowance,
            pbAmount: joiningAllowance,
          })
        }
        if (signOffAllowance > 0) {
          allowanceRows.push({
            label: 'Sign-Off Allowance',
            amount: signOffAllowance,
            pbAmount: signOffAllowance,
          })
        }

        const earnings: SalarySlipLineItem[] = [
          {
            label: 'Basic Wages',
            amount: round2(basicWages),
            pbAmount: round2(basicWages * pbFactor),
          },
          {
            label: 'Fixed OT (109 hrs)',
            amount: round2(overtimeWages),
            pbAmount: round2(overtimeWages * pbFactor),
          },
          {
            label: 'Leave Days',
            amount: round2(leaveWages),
            pbAmount: round2(leaveWages * pbFactor),
          },
          {
            label: 'Others',
            amount: round2(otherWages),
            pbAmount: round2(otherWages),
          },
          {
            label: 'Total Reimbursements',
            amount: round2(reimbursements),
            pbAmount: round2(reimbursements),
          },
          {
            label: 'HRA',
            amount: round2(hra),
            pbAmount: round2(hra),
          },
          ...allowanceRows,
        ]

        const earningsTotal = round2(earnings.reduce((sum, row) => sum + row.amount, 0))
        const deductionsGrandTotal = round2(allotmentAmount + totalDeductions)
        const balanceCarriedForward = round2(earningsTotal - deductionsGrandTotal)

        setSlipData({
          crewName: crewNameLive,
          rankName: rankNameLive,
          vesselName: vesselNameLive,
          currencyCode,
          dateOfJoining: formatDate(dateOfJoiningRaw),
          signOnDate: formatDate(signOnRecord?.signOnDate),
          signOnPort: signOnRecord?.portSignOn || '--',
          signOffDate: formatDate(signOffRecord?.signOffDate),
          signOffPort: signOffRecord?.portSignOff || '--',
          signOffReason: signOffRecord?.signOffReason || '--',
          arrivalHomeport: '--',
          accountNumber: banking?.accountNumber || '--',
          beneficiary: banking?.nameOfBeneficiary || '--',
          paidDate: '--',
          transactionRef: '--',
          earnings,
          allotmentAmount: round2(allotmentAmount),
          totalDeductions: round2(totalDeductions),
          earningsTotal,
          deductionsGrandTotal,
          balanceCarriedForward,
        })
      } catch (error) {
        if (!isMounted) return
        console.error('Error loading salary slip data:', error)
        setErrorMessage('Unable to load salary slip data right now.')
        showErrorToast(error, 'Unable to load salary slip data right now.', 'load')
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadSalarySlip()

    return () => {
      isMounted = false
    }
  }, [isOpen, selectedCrewId, selectedMonthPeriod, claimMonth, crewOptions])

  if (!isOpen) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      role='dialog'
      aria-modal='true'
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      onClick={onClose}
    >
      <div className='modal-dialog modal-xl' role='document' onClick={(event) => event.stopPropagation()}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Salary Slip Preview</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
          </div>

          <div className='modal-body' style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className='row g-3 mb-4'>
              <div className='col-md-8'>
                <label className='form-label fw-semibold'>Crew</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCrewId || ''}
                  onChange={(event) => setSelectedCrewId(Number(event.target.value || 0))}
                >
                  {crewOptions.length === 0 ? (
                    <option value=''>No crew available</option>
                  ) : (
                    crewOptions.map((option) => (
                      <option key={option.crewId} value={option.crewId}>
                        {option.crewName} {option.rankName ? `- ${option.rankName}` : ''}
                      </option>
                    ))
                  )}
                </SearchableSelect>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold'>Month</label>
                <input
                  type='month'
                  className='form-control'
                  value={selectedMonthPeriod}
                  onChange={(event) => setSelectedMonthPeriod(event.target.value)}
                />
              </div>
            </div>

            {isLoading ? (
              <div className='text-center py-10 text-muted'>Loading salary slip...</div>
            ) : errorMessage ? (
              <div className='alert alert-danger mb-0'>{errorMessage}</div>
            ) : !slipData ? (
              <div className='alert alert-warning mb-0'>No data found for selected crew and month.</div>
            ) : (
              <div className='border rounded p-4 bg-light'>
                <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4'>
                  <div>
                    <h4 className='mb-1'>Pay Slip</h4>
                  </div>
                  <div className='text-end'>
                    <div className='fw-semibold'>{slipData.vesselName}</div>
                    <div className='fw-bold'>{formatMonthTitle(selectedMonthPeriod)}</div>
                  </div>
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <div className='border rounded p-3 h-100 bg-white'>
                      <div className='fw-bold mb-2'>Crew Details</div>
                      <div><span className='text-muted'>Name:</span> {slipData.crewName}</div>
                      <div><span className='text-muted'>Rank:</span> {slipData.rankName}</div>
                      <div><span className='text-muted'>Date of Aggrement:</span> {slipData.dateOfJoining}</div>
                      <div><span className='text-muted'>Sign-On Date:</span> {slipData.signOnDate}</div>
                      <div><span className='text-muted'>Sign-On Port:</span> {slipData.signOnPort}</div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className='border rounded p-3 h-100 bg-white'>
                      <div className='fw-bold mb-2'>Voyage Details</div>
                      <div><span className='text-muted'>Sign-Off Date:</span> {slipData.signOffDate}</div>
                      <div><span className='text-muted'>Sign-Off Port:</span> {slipData.signOffPort}</div>
                      <div><span className='text-muted'>Sign-Off Reason:</span> {slipData.signOffReason}</div>
                      <div><span className='text-muted'>Arrival Homeport:</span> {slipData.arrivalHomeport}</div>
                      <div><span className='text-muted'>Currency:</span> {slipData.currencyCode}</div>
                    </div>
                  </div>
                </div>

                <div className='row g-3'>
                  <div className='col-lg-7'>
                    <div className='table-responsive border rounded bg-white'>
                      <table className='table table-sm table-row-bordered align-middle mb-0'>
                        <thead>
                          <tr className='fw-bold text-gray-700'>
                            <th>Earnings</th>
                            <th className='text-end'>Amount</th>
                            <th className='text-end'>Cont-to-PB Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slipData.earnings.map((item) => (
                            <tr key={item.label}>
                              <td>{item.label}</td>
                              <td className='text-end'>{toMoney(item.amount)}</td>
                              <td className='text-end'>{toMoney(item.pbAmount)}</td>
                            </tr>
                          ))}
                          <tr className='fw-bold'>
                            <td>Total</td>
                            <td className='text-end'>{toMoney(slipData.earningsTotal)}</td>
                            <td className='text-end'>{toMoney(slipData.earningsTotal)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className='col-lg-5'>
                    <div className='table-responsive border rounded bg-white'>
                      <table className='table table-sm table-row-bordered align-middle mb-0'>
                        <thead>
                          <tr className='fw-bold text-gray-700'>
                            <th>Deductions</th>
                            <th className='text-end'>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Allotment</td>
                            <td className='text-end'>{toMoney(slipData.allotmentAmount)}</td>
                          </tr>
                          <tr>
                            <td>Total Deductions</td>
                            <td className='text-end'>{toMoney(slipData.totalDeductions)}</td>
                          </tr>
                          <tr className='fw-bold'>
                            <td>Grand Total</td>
                            <td className='text-end'>{toMoney(slipData.deductionsGrandTotal)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className='mt-4 px-3 py-2 border rounded bg-white fw-semibold'>
                  Balance carried forward to next month: {toMoney(slipData.balanceCarriedForward)}
                </div>

                <div className='d-flex justify-content-end mt-5'>
                  <div className='text-center' style={{ minWidth: '220px' }}>
                    {signatureDataUrl ? (
                      <div className='mb-2 p-2 border rounded bg-white'>
                        <img
                          src={signatureDataUrl}
                          alt='Master Signature'
                          style={{ width: '200px', height: '70px', objectFit: 'contain' }}
                        />
                      </div>
                    ) : null}
                    <div style={{ borderTop: '1px solid #5e6278', marginBottom: '0.35rem' }} />
                    <div className='fw-semibold text-gray-800'>Master Signature</div>
                  </div>
                </div>

                {canManageSignature ? (
                  <div className='d-flex justify-content-end mt-3'>
                    <div className='border rounded p-3 bg-white' style={{ minWidth: '320px', maxWidth: '360px' }}>
                      <div className='fw-semibold mb-2'>Signature Attachment</div>
                      <input
                        type='file'
                        accept='image/*'
                        className='form-control form-control-sm'
                        onChange={handleSignatureFileChange}
                        disabled={isSignatureLoading || isSignatureSaving}
                      />
                      {signatureFileName ? <div className='text-muted fs-8 mt-1'>{signatureFileName}</div> : null}
                      {isSignatureLoading ? <div className='text-muted fs-8 mt-1'>Loading saved signature...</div> : null}

                      {pendingSignatureDataUrl ? (
                        <div className='mt-2 p-2 border rounded bg-light'>
                          <img
                            src={pendingSignatureDataUrl}
                            alt='Signature Preview'
                            style={{ width: '100%', maxHeight: '90px', objectFit: 'contain' }}
                          />
                        </div>
                      ) : null}

                      <div className='d-flex justify-content-end gap-2 mt-3'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          onClick={handleSaveSignature}
                          disabled={!pendingSignatureDataUrl || isSignatureLoading || isSignatureSaving}
                        >
                          {isSignatureSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={handleRemoveSignature}
                          disabled={(!signatureDataUrl && !pendingSignatureDataUrl) || isSignatureLoading || isSignatureSaving}
                        >
                          Remove
                        </button>
                      </div>

                      {signatureMessage ? <div className='text-muted fs-8 mt-2'>{signatureMessage}</div> : null}
                    </div>
                  </div>
                ) : (
                  <div className='d-flex justify-content-end mt-3'>
                    <div className='text-muted fs-8'>Only Master can upload or remove signature.</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleDownloadPdf}
              disabled={!slipData || isLoading || isPdfGenerating}
            >
              {isPdfGenerating ? 'Generating PDF...' : 'Download PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalarySlipModal
